import { useState, useCallback } from "react";

export interface UseInfiniteScrollPageOptions<T> {
  data: T[];
  initialLoadCount?: number;
  loadMoreCount?: number;
  onDataLoad?: (currentCount: number, totalCount: number) => Promise<void> | void;
  hasMore?: boolean; // 외부 hasMore 추가
}

export interface UseInfiniteScrollPageReturn<T> {
  // 테이블 props
  tableProps: {
    data: T[];
    infiniteScroll: boolean;
    initialLoadCount: number;
    loadMoreCount: number;
    hasMore: boolean;
    onLoadMore: (currentCount: number) => Promise<void>;
  };

  // 테이블 key (React key prop 경고 해결을 위해 분리)
  tableKey: number;

  // 상태
  hasMore: boolean;

  // 액션
  resetInfiniteScroll: () => void;
  handleSearch: () => void;
  handleReset: () => void;
}

export function useInfiniteScrollPage<T>({
  data,
  initialLoadCount = 20,
  loadMoreCount = 20,
  onDataLoad,
  hasMore: externalHasMore, // 외부 hasMore 받기
}: UseInfiniteScrollPageOptions<T>): UseInfiniteScrollPageReturn<T> {
  const [internalHasMore, setInternalHasMore] = useState(data.length > initialLoadCount);
  const [tableKey, setTableKey] = useState(0);

  // 외부 hasMore가 있으면 사용, 없으면 내부 상태 사용
  const hasMore = externalHasMore !== undefined ? externalHasMore : internalHasMore;

  // 무한 스크롤 핸들러
  const handleLoadMore = useCallback(
    async (currentCount: number) => {
      try {
        // 사용자 정의 데이터 로드 함수 호출 (API 호출 등)
        if (onDataLoad) {
          await onDataLoad(currentCount, data.length);
        }

        // 기본 로직: 현재 로드된 데이터 수가 전체 데이터 수와 같거나 크면 더 이상 로드할 데이터가 없음
        if (currentCount >= data.length) {
          setInternalHasMore(false);
        } else {
          setInternalHasMore(true);
        }
      } catch (error) {
        console.error("무한 스크롤 데이터 로드 중 오류:", error);
      }
    },
    [data.length, onDataLoad]
  );

  // 무한 스크롤 리셋
  const resetInfiniteScroll = useCallback(() => {
    setInternalHasMore(true);
    setTableKey(prev => prev + 1);
  }, []);

  // 검색 핸들러
  const handleSearch = useCallback(() => {
    resetInfiniteScroll();
  }, [resetInfiniteScroll]);

  // 리셋 핸들러
  const handleReset = useCallback(() => {
    resetInfiniteScroll();
  }, [resetInfiniteScroll]);

  // 테이블에 전달할 props
  const tableProps = {
    data,
    infiniteScroll: true,
    initialLoadCount,
    loadMoreCount,
    hasMore,
    onLoadMore: handleLoadMore,
  };

  return {
    tableProps,
    tableKey,
    hasMore,
    resetInfiniteScroll,
    handleSearch,
    handleReset,
  };
}
